import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLunchOrders } from '../../hooks/useLunch';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';

const LunchOrders: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const dateString = selectedDate.toISOString().split('T')[0];
  const { data: orders, isLoading } = useLunchOrders(dateString);

  const totalOrders = orders?.length || 0;
  const totalAmount = orders?.reduce((sum, order) => sum + order.totalPrice, 0) || 0;
  const itemSummaryMap: Record<string, number> = (orders || []).reduce((acc, order) => {
    order.items.forEach((item: any) => {
      const itemName = item.item.name;
      acc[itemName] = (acc[itemName] || 0) + 1;
    });
    return acc;
  }, {} as Record<string, number>);

  const itemSummary: [string, number][] = (Object.entries(itemSummaryMap) as [string, number][])
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

  return (
    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      <Box sx={{ flex: '1 1 350px', minWidth: 300 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Select Date
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose a date to view all orders
            </Typography>
            <LocalizationProvider 
              dateAdapter={AdapterDateFns}
              adapterLocale={{...enUS, options: { ...enUS.options, weekStartsOn: 1 }}}
            >
              <DateCalendar
                value={selectedDate}
                onChange={(newDate) => newDate && setSelectedDate(newDate)}
              />
            </LocalizationProvider>

            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" fontWeight="bold">
                    Total Orders:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {totalOrders}
                  </Typography>
                </Box>
              </Paper>
              <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" fontWeight="bold">
                    Total Amount:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {totalAmount} RSD
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ flex: '2 1 600px', minWidth: 300 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Orders for {format(selectedDate, 'dd.MM.yyyy.')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              List of all employee orders for this date
            </Typography>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : !orders || orders.length === 0 ? (
              <Alert severity="info">
                No orders placed for this date yet.
              </Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Employee</strong></TableCell>
                      <TableCell><strong>Items</strong></TableCell>
                      <TableCell><strong>Note</strong></TableCell>
                      <TableCell align="right"><strong>Price</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          {order.employee?.firstName} {order.employee?.lastName}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ maxWidth: 400 }}>
                            {order.items.map((item: any, index: number) => (
                              <Typography key={item.id} variant="body2">
                                {index + 1}. {item.item.name}
                              </Typography>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontStyle: order.note ? 'normal' : 'italic', color: order.note ? 'text.primary' : 'text.secondary' }}>
                            {order.note || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {order.totalPrice} RSD
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          Items Summary
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 400 }}>
                          {itemSummary.map(([itemName, count]) => (
                            <Typography key={itemName} variant="body2" fontWeight="medium">
                              {itemName}: {count}
                            </Typography>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell />
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {itemSummary.reduce((sum, [, count]) => sum + count, 0)} items
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default LunchOrders;
